import { VippsLoginBlock } from '@components/SectionBlocks/blocks/VippsLogin';
import { BlockContentContext } from '../BlockContent.utils';
import ArticleListSerializer from './serializers/ArticleListSerializer';
import { ImageSerializer } from './serializers/ImageSerializer';
import { ReferencedComponentSerializer } from './serializers/ReferencedComponentSerializer';
import { VehicleInspectionSerializer } from './serializers/VehicleInspectionSerializer';
import { VideoSerializer } from './serializers/VideoSerializer';
import { WideImageSerializer } from './serializers/WideImageSerializer';
import { YoutubeSerializer } from './serializers/YoutubeSerializer';
import {
  AccordionListBlockSerializer,
  AccordionSerializer,
  ActiveChargingTagsSerializer,
  AnchorSerializer,
  CarLoanCalculatorSerializer,
  CodeTypesSerializer,
  CompetitionSerializer,
  ConsumerTestProductListSerializer,
  CtaButtonBodySerializer,
  CtaButtonInternalLinkSerializer,
  CtaButtonLinkSerializer,
  CtaFileLinkSerializer,
  CtaGlobalBlockSerializer,
  DataWrapperSerializer,
  DocumentsBlockSerializer,
  DynamicFormSelectorSerializer,
  ExternalLinkSerializer,
  FAQGlobalReferenceSerializer,
  FormSerializer,
  HighChartsSerializer,
  IdpCountriesListSerializer,
  IframeSerializer,
  ImageCarouselSerializer,
  ImagesSerializer,
  InfoBoxSerializer,
  InsuranceCalculatorSerializer,
  InternalLinkSerializer,
  ModularButtonBlockSerializer,
  NumberedListSerializer,
  NumberOfUsedRoadAssistanceSerializer,
  OilChangeCalculatorSerializer,
  ParallaxSerializer,
  PayWallSerializer,
  PodcastSerializer,
  PollSerializer,
  PortraitsWithTextSerializer,
  ProsAndConsSerializer,
  QuizSerializer,
  QuoteSerializer,
  ShortsCarouselSerializer,
  SocialProofSerializer,
  StepByStepListSerializer,
  TableSerializer,
  TestCollectionSerializer,
  TestResultSerializer,
  WindShieldWiperChangeCalculatorSerializer,
} from './serializers/contextual';
import { MainAndHouseholdMembersListSerializer } from './components/MainAndHouseholdMembersList/MainAndHouseholdMembersList';
import { MembershipOverviewSerializer } from './components/MembershipOverview/MembershipOverview';

import { CampaignBlockSerializer } from './components/CampaignBlock/CampaignBlock';

import { MembershipValidUntilSerializer } from './components/MembershipValidUntil/MembershipValidUntil';

import { DoubleImageSerializer } from './components/DoubleImage/DoubleImage';
import { FeaturedVehicleResultSerializer } from './components/FeaturedVehicleResultSerializer/FeaturedVehicleResultSerializer';

import TravelSuggestionSerializer from './components/TravelSuggestionSerializer/TravelSuggestionSerializer';
import { WeatherForecastsSerializer } from './components/WeatherForecastsSerializer/WeatherForecastsSerializer';
import { AltitudeChartSerializer } from './components/AltitudeChartSerializer/AltitudeChartSerializer';
import { RouteMapSerializer } from './components/RouteMapSerializer/RouteMapSerializer';
import { MembershipValidUntilGreenBadgeSerializer } from './components/MembershipValidUntilGreenBadge/MembershipValidUntilGreenBadge';

import { InformationUserInteractionSerializer } from './components/InformationUserInteraction/InformationUserInteraction';
import { TextWithEndDateSerializer } from './components/TextWithEndDate/TextWithEndDate';
import { TextWithEmailSerializer } from './components/TextWithEmail/TextWithEmail';
import { GreenBoxWithMembersListAndEndDateSerializer } from './components/GreenBoxWithMembersListAndEndDate/GreenBoxWithMembersListAndEndDate';
import { TextWithHouseholdMemberSerializer } from './components/TextWithHouseholdMember/TextWithHouseholdMember';
import { InfoMessageBoxSerializer } from './components/InfoMessageBox/InfoMessageBox';

import { WarningUserInteractionSerializer } from './components/WarningUserInteraction/WarningUserInteraction';

import { CallButtonBlockSerializer } from './components/CallButton/CallButton';
import { SeparatorLineBlockSerializer } from './components/SeparatorLineBlock/SeparatorLineBlock';

export const types = (context: BlockContentContext = BlockContentContext.Stripped) => ({
  // Both spellings are live: `accordeonComponent` from articles, `accordeon` from product pages.
  // Removing either breaks that content — the schema has to settle on one spelling first.
  accordeon: AccordionSerializer(context),
  accordeonComponent: AccordionSerializer(context),
  accordionList: AccordionListBlockSerializer(context),
  activeChargingTags: ActiveChargingTagsSerializer(context),
  altitudeChart: AltitudeChartSerializer(),
  anchor: AnchorSerializer(context),
  articleList: ArticleListSerializer(context),
  bodyFeaturedVehicleTestResult: FeaturedVehicleResultSerializer(),
  bodyTestResult: TestResultSerializer(context),
  campaignBlockComponent: CampaignBlockSerializer(),
  carLoanCalculator: CarLoanCalculatorSerializer(context),
  code: CodeTypesSerializer(context),
  consumerTestProductList: ConsumerTestProductListSerializer(context),
  ctaButtonLinkComponent: CtaButtonLinkSerializer(context),
  ctaButtonBody: CtaButtonBodySerializer(context),
  ctaButtonInternalLinkComponent: CtaButtonInternalLinkSerializer(context),
  ctaExternalLinkComponent: ExternalLinkSerializer(context),
  ctaFileLinkComponent: CtaFileLinkSerializer(context),
  ctaGlobalBlock: CtaGlobalBlockSerializer(context),
  // Likewise: NavigationLinksBlock and useResolvedNavigationLink both branch on
  // `ctaInternalLinkComponent`.
  ctaInternalLink: InternalLinkSerializer(context),
  ctaInternalLinkComponent: InternalLinkSerializer(context),
  dataTableComponent: TableSerializer(context),
  dataWrapperComponent: DataWrapperSerializer(context),
  documentsBlock: DocumentsBlockSerializer(context),
  doubleImage: DoubleImageSerializer(),
  dynamicForm: FormSerializer(context),
  dynamicFormSelectorReference: DynamicFormSelectorSerializer(context),
  highcharts: HighChartsSerializer(context),
  ifkCountriesList: IdpCountriesListSerializer(context),
  iframeComponent: IframeSerializer(context),
  imageCarousel: ImageCarouselSerializer(context),
  imageComponent: ImageSerializer(context),
  imagesComponent: ImagesSerializer(context),
  infoBox: InfoBoxSerializer(context),
  insuranceCalculator: InsuranceCalculatorSerializer(context),
  membershipValidUntil: MembershipValidUntilSerializer(),
  membershipValidUntilGreenBadge: MembershipValidUntilGreenBadgeSerializer(),
  membershipOverview: MembershipOverviewSerializer(),
  informationUserInteraction: InformationUserInteractionSerializer(),
  warningUserInteraction: WarningUserInteractionSerializer(),
  textWithEndDate: TextWithEndDateSerializer(),
  textWithEmail: TextWithEmailSerializer(),
  textWithHouseholdMember: TextWithHouseholdMemberSerializer(),
  mainAndHouseholdMembersList: MainAndHouseholdMembersListSerializer(),
  greenBoxWithMembersListAndEndDate: GreenBoxWithMembersListAndEndDateSerializer(),
  numberOfUsedRoadAssistance: NumberOfUsedRoadAssistanceSerializer(context),
  numberedListWithImage: NumberedListSerializer(context),
  oilChangeCalculator: OilChangeCalculatorSerializer(context),
  podcastEpisode: PodcastSerializer(context),
  portraitsWithText: PortraitsWithTextSerializer(context),
  quizComponent: QuizSerializer(context),
  quoteComponent: QuoteSerializer(context),
  socialProofComponent: SocialProofSerializer(context),
  referencedComponent: ReferencedComponentSerializer(context),
  routeMap: RouteMapSerializer(),
  scrollableTextBlocksWithBackgroundImages: ParallaxSerializer(context),
  testCollectionComponent: TestCollectionSerializer(context),
  travelSuggestion: TravelSuggestionSerializer(),
  vehicleInspection: VehicleInspectionSerializer(context),
  videoComponent: VideoSerializer(context),
  weatherForecasts: WeatherForecastsSerializer(),
  wideImage: WideImageSerializer(context),
  windshieldWiperChangeCalculator: WindShieldWiperChangeCalculatorSerializer(context),
  youtubeEmbed: YoutubeSerializer(context),
  youtubeShorts: ShortsCarouselSerializer(context),
  vippsLogin: VippsLoginBlock,
  infoMessageBox: InfoMessageBoxSerializer(),
  prosAndCons: ProsAndConsSerializer(context),
  paywallReference: PayWallSerializer(context),
  stepBystepList: StepByStepListSerializer(context),
  modularButton: ModularButtonBlockSerializer(context),
  callButtonComponent: CallButtonBlockSerializer(),
  separatorLine: SeparatorLineBlockSerializer(),
  faqGlobalReference: FAQGlobalReferenceSerializer(context),
  pollReference: PollSerializer(context),
  competitionReference: CompetitionSerializer(context),
});
